import { useEffect, useState } from "react";
import { CheckCircle2, AlertTriangle, XCircle } from "lucide-react";
import Logo from "components/Logo";
import AdminCard from "components/admin/AdminCard";
import { getPublicStatus, PublicStatusResponse } from "services/status/status.service";

const STATE_CONFIG: Record<string, { label: string; icon: JSX.Element; color: string }> = {
  operational: { label: "Operacional", icon: <CheckCircle2 className="w-5 h-5 text-green-600" />, color: "text-green-600" },
  maintenance: { label: "Manutenção", icon: <AlertTriangle className="w-5 h-5 text-yellow-600" />, color: "text-yellow-600" },
  instability: { label: "Instabilidade", icon: <XCircle className="w-5 h-5 text-red-600" />, color: "text-red-600" },
};

const OVERALL_MESSAGE: Record<string, string> = {
  operational: "Todos os sistemas operacionais",
  maintenance: "Manutenção programada em andamento",
  instability: "Estamos enfrentando instabilidade em alguns serviços",
};

const StatusPage = () => {
  const [status, setStatus] = useState<PublicStatusResponse | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getPublicStatus()
      .then(setStatus)
      .catch((err) => console.error("[StatusPage] erro:", err))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen w-full bg-slate-50 py-16 px-4">
      <div className="max-w-2xl mx-auto flex flex-col gap-6">
        <div className="flex justify-center mb-2">
          <Logo />
        </div>

        {loading ? (
          <p className="text-center text-sm text-muted-foreground">Carregando...</p>
        ) : status ? (
          <>
            <AdminCard className="flex items-center gap-3 justify-center py-5">
              {STATE_CONFIG[status.overall]?.icon}
              <p className={`text-base font-semibold ${STATE_CONFIG[status.overall]?.color}`}>
                {OVERALL_MESSAGE[status.overall]}
              </p>
            </AdminCard>

            <AdminCard title="Serviços">
              <ul className="flex flex-col divide-y divide-slate-100">
                {status.integrations.map((i) => (
                  <li key={i.key} className="flex items-center justify-between py-2.5 first:pt-0 last:pb-0">
                    <span className="text-sm font-medium text-slate-700">{i.label}</span>
                    <span className={`flex items-center gap-1.5 text-sm font-medium ${STATE_CONFIG[i.state]?.color}`}>
                      {STATE_CONFIG[i.state]?.icon}
                      {STATE_CONFIG[i.state]?.label}
                    </span>
                  </li>
                ))}
              </ul>
            </AdminCard>

            {status.announcements.length > 0 && (
              <AdminCard title="Comunicados">
                <ul className="flex flex-col gap-4">
                  {status.announcements.map((a) => (
                    <li key={a.id}>
                      <p className="text-sm font-medium text-slate-800">{a.title}</p>
                      <p className="text-sm text-slate-500">{a.body}</p>
                    </li>
                  ))}
                </ul>
              </AdminCard>
            )}

            {status.incidents.length > 0 && (
              <AdminCard title="Histórico recente">
                <ul className="flex flex-col divide-y divide-slate-100">
                  {status.incidents.map((inc) => (
                    <li key={inc.id} className="py-2.5 first:pt-0 last:pb-0 text-sm">
                      <span className="font-medium text-slate-700">{inc.integrationKey}</span>{" "}
                      <span className="text-slate-500">
                        {inc.previousState ? `${inc.previousState} → ` : ""}
                        {inc.newState}
                      </span>
                      {inc.message && <p className="text-slate-400">{inc.message}</p>}
                      <p className="text-xs text-slate-400">{new Date(inc.createdAt).toLocaleString("pt-BR")}</p>
                    </li>
                  ))}
                </ul>
              </AdminCard>
            )}
          </>
        ) : (
          <p className="text-center text-sm text-muted-foreground">Não foi possível carregar o status.</p>
        )}
      </div>
    </div>
  );
};

export default StatusPage;
